import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { RMSButton } from 'features/shared/components/buttons';
import { executeApiAction } from 'features/shared/utility/apiHelper';
import { deleteAddressRecord } from 'features/user/services/addressService';
import { getAddressSops } from 'features/user/services/sopService';
import { useDispatch } from 'react-redux';
import { notify } from 'reducers/NotifierReducer';
import { RMSCommonDialog, RMSConfirmationDialog } from 'features/shared/components/dialog';
import useConfirmDialog from 'features/shared/utility/useConfirmDialog';
import { RMSMenuSidebar } from 'features/shared/components/sidebars';
import RMSRecordHistoryButton from 'features/shared/components/modules/RMSRecordHistoryButton';
import {
  AddressMenuItemKey,
  AddressSidebarGroups,
  getSelectedAddressMenu,
  EDITABLE_ADDRESS_MENU_KEYS,
} from 'features/user/constants/addressSideBarMenuHelper';
import { useAddressDialog } from 'features/user/hooks/useAddressDialog';
import {
  addressDialogSaved,
  addressDialogDeleted,
} from 'features/user/store/actions/addressDialog.actions';

const AddEditAddressPage = ({ dialog }) => {
  const dispatch = useDispatch();
  const { addressId: ptsAddressID, requestId, closeOnSave, copyFromAddressId } = dialog;
  const { closeAddressDialog } = useAddressDialog();
  const open = true;
  const onClose = useCallback(() => closeAddressDialog(requestId), [closeAddressDialog, requestId]);
  const savedAddressIdRef = useRef(ptsAddressID);
  const detailsRef = useRef(null);
  const coordinatesRef = useRef(null);
  const prePlanRef = useRef(null);

  const [addressId, setAddressId] = useState(ptsAddressID);
  const [currentAddressText, setCurrentAddressText] = useState('');
  const [activeKey, setActiveKey] = useState(AddressMenuItemKey.EDIT_ADDRESS);
  const [historyState, setHistoryState] = useState(null);
  const historyRecord = historyState?.menuKey === activeKey ? historyState.record : null;
  const [formState, setFormState] = useState({ isDirty: false, submitting: false, canSave: false });
  const [deleting, setDeleting] = useState(false);
  const [sopCount, setSopCount] = useState(0);
  const [sopRefreshKey, setSopRefreshKey] = useState(0);

  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const activeMenu = getSelectedAddressMenu(activeKey);
  const ActiveContent = activeMenu?.content;
  const saveEnabled = EDITABLE_ADDRESS_MENU_KEYS.has(activeKey);
  const addressPermissions = usePermissions(PERMISSION_RESOURCES.ADDRESS);
  const canDeleteAddress = addressPermissions.Delete;
  const isViewOnly = Boolean(addressId && !addressPermissions.Edit);

  const handleSopsChanged = useCallback(() => setSopRefreshKey((key) => key + 1), []);

  const getActiveRef = useCallback(() => {
    if (activeKey === AddressMenuItemKey.COORDINATES) return coordinatesRef;
    if (activeKey === AddressMenuItemKey.PRE_PLAN) return prePlanRef;
    if (activeKey === AddressMenuItemKey.EDIT_ADDRESS) return detailsRef;
    return null;
  }, [activeKey]);

  const handleStateChange = useCallback(
    (state) => {
      setHistoryState({
        menuKey: activeKey,
        record: state?.recordHistory || state?.coordinate || state?.prePlan || null,
      });
      setFormState({
        isDirty: Boolean(state?.isDirty),
        submitting: Boolean(state?.submitting),
        canSave: Boolean(state?.canSave),
      });
      if (state?.ptsAddressID) setAddressId(state.ptsAddressID);
      if (state?.fullAddressText !== undefined) setCurrentAddressText(state.fullAddressText || '');
    },
    [activeKey]
  );

  const handleAddressSaved = useCallback(
    (savedAddress) => {
      const savedId = savedAddress?.ptsAddressID || savedAddress?.id;
      if (savedId) setAddressId(savedId);
      if (savedAddress?.FullAddressText) setCurrentAddressText(savedAddress.FullAddressText);
      const isEditMode = Boolean(savedAddressIdRef.current);
      savedAddressIdRef.current = savedId;
      dispatch(addressDialogSaved(savedAddress, requestId, isEditMode));
      if (closeOnSave) closeAddressDialog(requestId);
    },
    [dispatch, requestId, closeOnSave, closeAddressDialog]
  );

  const handleMenuSelect = useCallback(
    (nextKey) => {
      if (nextKey === activeKey) return;
      if (formState.submitting) {
        dispatch(notify('Please wait for the current save to finish.', 'warning'));
        return;
      }

      const changeMenu = () => {
        if (!addressId && nextKey !== AddressMenuItemKey.EDIT_ADDRESS) {
          dispatch(notify('Save the address first to access its other tabs.', 'warning'));
          return;
        }
        setActiveKey(nextKey);
        setFormState({ isDirty: false, submitting: false, canSave: false });
      };
      const activeRef = getActiveRef();
      if (EDITABLE_ADDRESS_MENU_KEYS.has(activeKey) && activeRef?.current?.isDirty?.()) {
        requestConfirm(
          'You have unsaved changes on this tab. Are you sure you want to leave without saving?',
          changeMenu,
          'Unsaved Changes'
        );
        return;
      }
      changeMenu();
    },
    [activeKey, addressId, dispatch, formState.submitting, getActiveRef, requestConfirm]
  );

  const handleSave = useCallback(() => {
    getActiveRef()?.current?.save?.();
  }, [getActiveRef]);

  const handleDelete = useCallback(() => {
    if (!addressId || !canDeleteAddress || deleting) return;

    requestConfirm(
      `Are you sure you want to delete "${currentAddressText || 'this address'}"?`,
      async () => {
        await executeApiAction({
          setLoading: setDeleting,
          dispatch,
          action: () => deleteAddressRecord(addressId),
          onSuccess: () => {
            dispatch(notify('Address deleted successfully', 'success'));
            dispatch(addressDialogDeleted(addressId, requestId));
            onClose?.();
          },
        });
      },
      'Delete Address'
    );
  }, [
    addressId,
    canDeleteAddress,
    deleting,
    requestConfirm,
    currentAddressText,
    dispatch,
    requestId,
    onClose,
  ]);

  const handleClose = useCallback(() => {
    if (formState.submitting || deleting) return;
    if (EDITABLE_ADDRESS_MENU_KEYS.has(activeKey) && getActiveRef()?.current?.isDirty?.()) {
      requestConfirm(
        'You have unsaved changes on this tab. Are you sure you want to close without saving?',
        onClose,
        'Unsaved Changes'
      );
      return;
    }
    onClose?.();
  }, [activeKey, formState.submitting, deleting, getActiveRef, onClose, requestConfirm]);

  useEffect(() => {
    if (!open) return;
    setAddressId(ptsAddressID || null);
    setCurrentAddressText('');
    setActiveKey(AddressMenuItemKey.EDIT_ADDRESS);
    setFormState({ isDirty: false, submitting: false, canSave: false });
  }, [open, ptsAddressID]);

  useEffect(() => {
    let active = true;
    setSopCount(0);
    if (!addressId) return undefined;
    executeApiAction({
      action: () => getAddressSops(addressId),
      onSuccess: (rows) => {
        if (active) setSopCount(Array.isArray(rows) ? rows.length : 0);
      },
    });
    return () => {
      active = false;
    };
  }, [addressId, sopRefreshKey]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        fullScreen
        title={isViewOnly ? 'View Address' : addressId ? 'Edit Address' : 'Add Address'}
        onClose={handleClose}
        closeOnBackdropClick={false}
        maxWidth="xl"
        fullWidth
        className="add-edit-address-page-content"
        dialogClassName="add-edit-address-dialog"
        saveButton={{
          show: saveEnabled,
          type: 'simple',
          label: 'Save',
          color: 'primary',
        }}
        onSave={handleSave}
        loading={formState.submitting || deleting}
        saveDisabled={!formState.isDirty || !formState.canSave || formState.submitting || deleting}
        cancelButton={{ show: true, type: 'simple', label: 'Close' }}
        leftActions={
          <>
            {addressId && EDITABLE_ADDRESS_MENU_KEYS.has(activeKey) && (
              <RMSRecordHistoryButton
                key={activeKey}
                disabled={!historyRecord?.Created || formState.submitting || deleting}
                created={historyRecord?.Created}
                createdBy={historyRecord?.CreatedBy}
                updated={historyRecord?.Updated}
                updatedBy={historyRecord?.UpdatedBy}
              />
            )}
            {addressId && canDeleteAddress && (
              <RMSButton
                label="Delete"
                prefixIcon={<DeleteIcon />}
                variant="outlined"
                className="add-edit-address-page__delete-button"
                onClick={handleDelete}
                disabled={deleting || formState.submitting}
              />
            )}
          </>
        }>
        <div className="add-edit-address-page">
          <RMSMenuSidebar
            activeKey={activeKey}
            onSelect={handleMenuSelect}
            groups={AddressSidebarGroups}
            className="app-sidebar--bordered"
            filterLabel="Filter address menu"
            renderItemLabel={(item) =>
              item.key === AddressMenuItemKey.SOPS && sopCount > 0
                ? 'SOPs (' + sopCount + ')'
                : item.label
            }
            getItemClassName={(item) =>
              item.key === AddressMenuItemKey.SOPS && sopCount > 0
                ? 'app-sidebar__item--alert'
                : undefined
            }
          />
          <main className="add-edit-address-page__content">
            {currentAddressText && (
              <div className="add-edit-address-page__address-summary">
                <Typography variant="subtitle1">{currentAddressText}</Typography>
              </div>
            )}
            {ActiveContent ? (
              <ActiveContent
                ref={
                  activeKey === AddressMenuItemKey.EDIT_ADDRESS
                    ? detailsRef
                    : activeKey === AddressMenuItemKey.COORDINATES
                    ? coordinatesRef
                    : activeKey === AddressMenuItemKey.PRE_PLAN
                    ? prePlanRef
                    : undefined
                }
                ptsAddressID={addressId}
                copyFromAddressId={copyFromAddressId}
                fullAddressText={currentAddressText}
                onStateChange={handleStateChange}
                onSaved={handleAddressSaved}
                onSopsChanged={
                  activeKey === AddressMenuItemKey.SOPS ? handleSopsChanged : undefined
                }
              />
            ) : (
              <Typography variant="h6">{activeMenu?.label}</Typography>
            )}
          </main>
        </div>
      </RMSCommonDialog>
      <RMSConfirmationDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        onYes={handleYes}
        onNo={handleNo}
      />
    </>
  );
};

export default AddEditAddressPage;
